
import { BASE_URL } from "../Utils/Constants";
import { handleLogout } from "../Service/api/configuration/localstorage";
import { ErrorToast } from "./Toast";

const AccessToken = async () => {
  let token = await localStorage.getItem("token");
  if (token) {
    return `Bearer ${token}`;
  }
  return null;
};

const ApiCall = async (endPoint, Method, params, headers) => {
  headers = {
    ...(typeof params === "string" && { "Content-Type": "application/json" }),
    ...((await AccessToken()) && { Authorization: await AccessToken() }),
    ...headers,
  };
  return fetch(`${BASE_URL + endPoint}`, {
    method: Method,
    headers: headers,
    ...(params && { body: params }),
  }).then(async (response) => {
    let data;
    try {
      data = await response.json();
    } catch (e) {
      data = {};
    }

    if (response.status === 401) {
      if (
        data?.code === 'SESSION_EXPIRED' ||
        data?.code === 'ACCOUNT_DEACTIVATED' ||
        data?.message === 'Session expired. Please Logout and login again.' ||
        data?.error === 'Session expired. Please login again.'
      ) {
        ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
        setTimeout(() => {
          handleLogout();
        }, 1000);
      }
    }

    // Include the status code in the response
    return {
      ...data,
      status: response.status
    };
  });
};


export const API = {
  async Login(params) {
    return ApiCall("auth/login", "POST", params, {});
  },
  async CreateInventory(params) {
    return ApiCall("inventory/createBulk", "POST", params, {});
  },
  async GetInventory(params) {
    return ApiCall(`inventory?page=${params?.page || 1}&limit=${params?.limit}&status=${params?.status || 'Unassigned'}&search=${params?.search || ''}&startDate=${params?.startDate || ''}&endDate=${params?.endDate || ''}&distributor=${params?.distributor || ''}&agent=${params?.agent || ''}&VC=${params?.VC || ''}&isAgent=${params?.isAgent !== undefined ? params?.isAgent : ''}&userActivationStatus=${params?.userActivationStatus || ''}&isActivated=${params?.isActivated || ''}&sortBy=${params?.sortBy || ''}&sortOrder=${params?.sortOrder || ''}&holder=${params?.holder || ''}&dealer=${params?.dealer || ''}`, "GET", null, {});
  },
  async GetReport(params) {
    return ApiCall(`inventory/getInventoryReportExcel?page=${params?.page || 1}&limit=${params?.limit || 10}&search=${params?.search || ''}&status=${params?.status || ''}&type=${params?.type || ''}&startDate=${params?.startDate || ''}&endDate=${params?.endDate || ''}`, "GET", null, {});
  },
  // The FASTags behind one report row — works for every report type, including
  // Manual Undo, which has no downloadable file.
  async GetReportTags(reportId, params) {
    return ApiCall(`inventory/reportTags/${reportId}?page=${params?.page || 1}&limit=${params?.limit || 50}`, "GET", null, {});
  },
  async DownloadFile(body) {
    const response = await fetch(`${BASE_URL}inventory/downloadInventoryExcel`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await AccessToken()) && { Authorization: await AccessToken() },
      },
      body,
    });

    if (!response.ok) {
      if (response.status === 401) {
        try {
          const data = await response.json();
          if (
            data?.code === 'SESSION_EXPIRED' ||
            data?.code === 'ACCOUNT_DEACTIVATED' ||
            data?.message === 'Session expired. Please Logout and login again.' ||
            data?.error === 'Session expired. Please login again.'
          ) {
            ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
            setTimeout(() => {
              handleLogout();
            }, 1000);
          }
        } catch (e) { }
      }
      throw new Error("File download failed");
    }

    return await response.blob(); // ✅ return blob, not JSON
  },

  async Createpurchagefastag(params) {
    // Handle both FormData (with file) and JSON (without file)
    if (params instanceof FormData) {
      const response = await fetch(`${BASE_URL}purchagefastag`, {
        method: "POST",
        headers: {
          ...(await AccessToken()) && { Authorization: await AccessToken() },
          // Don't set Content-Type for FormData - browser will set it with boundary
        },
        body: params,
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        data = {};
      }

      if (response.status === 401) {
        if (
          data?.code === 'SESSION_EXPIRED' ||
          data?.code === 'ACCOUNT_DEACTIVATED' ||
          data?.message === 'Session expired. Please Logout and login again.' ||
          data?.error === 'Session expired. Please login again.'
        ) {
          ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
          setTimeout(() => {
            handleLogout();
          }, 1000);
        }
      }

      return {
        ...data,
        status: response.status
      };
    } else {
      // Fallback to existing ApiCall for JSON data
      return ApiCall("purchagefastag", "POST", params, {});
    }
  },

  async GetFastag(params) {
    return ApiCall(`purchagefastag?page=${params?.page || 1}&limit=${params?.limit}&search1=${params?.search1 || ''}&search2=${params?.search2 || ''}`, "GET", null, {});
  },

  async GetDistributorRequest(params) {
    return ApiCall(`purchagefastag/getDistributorRequest?page=${params?.page || 1}&limit=${params?.limit || 10}`, "GET", null, {});
  },

  async ApproveFastag(params) {
    return ApiCall("purchagefastag/approvedFasTag", "POST", params, {});
  },

  async DownloadReceipt(fileUrl) {
    const token = await AccessToken();
    const response = await fetch(`${BASE_URL}purchagefastag/download-receipt?fileUrl=${encodeURIComponent(fileUrl)}`, {
      method: "GET",
      headers: {
        ...(token && { Authorization: token }),
      },
    });

    if (!response.ok) {
      throw new Error("Failed to download file");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const filename = fileUrl.split("/").pop() || "receipt.png";
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async UploadFile(params) {
    return ApiCall("upload", "POST", params, {});
  },
  async CreateAgent(params) {
    return ApiCall("users/agent/create", "POST", params, {});
  },
  async LoginWithOtp(params) {
    return ApiCall("auth/loginWithOtp", "POST", params, {});
  },
  async GetAgents(params) {
    return ApiCall(`users/getagents?page=${params?.page || 1}&limit=${params?.limit}&search=${params?.search || ''}&username=${params?.username || ''}`, "GET", null, {});
  },
  async CreateDealer(params) {
    return ApiCall("users/dealer/create", "POST", params, {});
  },
  async GetDealers(params) {
    return ApiCall(`users/getdealers?page=${params?.page || 1}&limit=${params?.limit}&search=${params?.search || ''}&username=${params?.username || ''}&status=${params?.status || ''}`, "GET", null, {});
  },
  async EditDealer(dealerId, params) {
    return ApiCall(`users/dealer/${dealerId}`, "POST", params, {});
  },
  async GetAgentsNames(params) {
    const distributorId = params?.distributorId || (typeof params === 'string' ? params : '') || '';
    return ApiCall(`users/getagentsnames?distributorId=${distributorId}&dealerId=${params?.dealerId || ''}`, "GET", null, {});
  },
  async GetAllAgentsNames(params) {
    return ApiCall(`users/getagentnames`, "GET", null, {});
  },
  async GetAllDealersNames(params) {
    return ApiCall(`users/getdealernames?distributorId=${params?.distributorId || ''}`, "GET", null, {});
  },

  async GetInventoryDistributor(params) {
    return ApiCall(`inventory/distributor?page=${params?.page || 1}&limit=${params?.limit}&search=${params?.search || ''}&startDate=${params?.startDate || ''}&endDate=${params?.endDate || ''}&status=${params?.status || ''}&agent=${params?.agent || ''}&VC=${params?.VC || ''}&userActivationStatus=${params?.userActivationStatus || ''}&scope=${params?.scope || ''}&dealer=${params?.dealer || ''}&isActivated=${params?.isActivated || ''}&activationSort=${params?.activationSort || ''}&activationSortOrder=${params?.activationSortOrder || ''}`, "GET", null, {});
  },

  async GetDistributor(params) {
    return ApiCall(`users/getdistributors?page=${params?.page || 1}&limit=${params?.limit}&search=${params?.search || ''}&username=${params?.username || ''}`, "GET", null, {});
  },
  async GetDistributorNames(params) {
    return ApiCall(`users/getdistributorsnames`, "GET", null, {});
  },
  async CreateDistributor(params) {
    return ApiCall("users/distributor/create", "POST", params, {});
  },

  async AssignAgent(params) {
    return ApiCall("purchagefastag/assigntoagent", "POST", params, {});
  },
  async GetInventoryAgent(params) {
    return ApiCall(`inventory/agent?page=${params?.page || 1}&limit=${params?.limit}&search=${params?.search || ''}&startDate=${params?.startDate || ''}&endDate=${params?.endDate || ''}&isActivated=${params?.isActivated || ''}&VC=${params?.VC || ''}&activationSort=${params?.activationSort || ''}&activationSortOrder=${params?.activationSortOrder || ''}`, "GET", null, {});
  },

  async ActivateNow(params) {
    return ApiCall("agent/activatenow", "POST", params, {});
  },

  async GetState(params) {
    return ApiCall(`users/state`, "GET", null, {});
  },
  async GetCity(stateId) {
    return ApiCall(`users/city?stateId=${stateId}`, "GET", null, {});
  },
  async SendOtp(params) {
    return ApiCall("auth/sendOtp", "POST", params, {});
  },
  async ChangePassword(params) {
    return ApiCall("auth/resetPassword", "POST", params, {});
  },
  // Public distributor set/reset password flow (email + 48h token link)
  async ForgotPassword(params) {
    return ApiCall("auth/forgotPassword", "POST", params, {});
  },
  async VerifyResetToken(params) {
    return ApiCall("auth/verifyResetToken", "POST", params, {});
  },
  async SetPasswordWithToken(params) {
    return ApiCall("auth/setPassword", "POST", params, {});
  },
  async EditDistributor(distributorId, params) {
    return ApiCall(`users/distributor/${distributorId}`, "POST", JSON.stringify(params), {});
  },
  async UpdateAgent(agentId, params) {
    return ApiCall(`users/agent/${agentId}`, "POST", JSON.stringify(params), {});
  },
  // Lightweight edit of a user's email + permissions (agent/dealer/distributor).
  async EditUserPermissions(params) {
    const body = { userId: params?.userId, email: params?.email, canViewTransactions: params?.canViewTransactions };
    // Name + Active/Deactivate status — only include when provided.
    if (params?.username !== undefined) body.username = params.username;
    if (typeof params?.isActive === "boolean") body.isActive = params.isActive;
    // Agents always activate — only include the activation flag when provided.
    if (typeof params?.canActivateFastag === "boolean") body.canActivateFastag = params.canActivateFastag;
    return ApiCall("users/edit-user-permissions", "POST", JSON.stringify(body), {});
  },
  async UpdateInventory(inventoryId, params) {
    return ApiCall(`inventory/${inventoryId}`, "POST", JSON.stringify(params), {});
  },
  async UpdatePurchageFastag(id, params) {
    return ApiCall(`purchagefastag/${id}`, "POST", JSON.stringify(params), {});
  },
  async GetMasterFastag(params) {
    // Build URL with conditional parameters
    let url = `inventory?page=${params?.page || 1}&limit=${params?.limit || 10}`;

    // Only add status parameter if it has a value
    if (params?.status && params?.status !== '') {
      url += `&status=${params.status}`;
    }

    // Add other parameters
    url += `&search=${params?.search || ''}`;
    url += `&startDate=${params?.startDate || ''}`;
    url += `&endDate=${params?.endDate || ''}`;
    url += `&distributor=${params?.distributor || ''}`;
    url += `&agent=${params?.agent || ''}`;
    url += `&dealer=${params?.dealer || ''}`;
    url += `&VC=${params?.VC || ''}`;
    url += `&isActivated=${params?.isActivated || ''}`;
    // When showing only activated tags, sort by activation date (direction toggleable).
    if (params?.isActivated === 'true') {
      url += `&activationSort=true`;
      if (params?.activationSortOrder) {
        url += `&activationSortOrder=${params.activationSortOrder}`;
      }
    }

    console.log('[GetMasterFastag] URL:', url, '| params.activationSortOrder:', params?.activationSortOrder);
    return ApiCall(url, "GET", null, {});
  },
  async GetInventorySummary() {
    return ApiCall("inventory/adminSummary", "GET", null, {});
  },

  // Admin: external debit transactions (proxied by our backend).
  async GetDebitTransactions(params) {
    return ApiCall("inventory/debit-transactions", "POST", JSON.stringify({
      pageNumber: params?.pageNumber || 1,
      pageSize: params?.pageSize || 10,
      startDate: params?.startDate || "",
      endDate: params?.endDate || "",
      fastagId: params?.fastagId || "",
    }), {});
  },

  // Admin / Partner new debit transactions
  async GetNewDebitTransactions(params) {
    const body = {};
    if (params?.from) body.from = params.from;
    if (params?.to) body.to = params.to;
    if (params?.page) body.page = params.page;
    if (params?.page_size) body.page_size = params.page_size;
    if (params?.vehicle_number) body.vehicle_number = params.vehicle_number;

    let userDetail = {};
    try {
      userDetail = JSON.parse(localStorage.getItem('UserDetail') || localStorage.getItem('user') || '{}');
    } catch (e) { }

    const role = userDetail?.role;
    const uid = userDetail?._id;

    // Explicitly set the proper role ID into request body payload
    if (role === 'distributor' && uid) {
      body.distributorId = uid;
    } else if (params?.distributorId) {
      body.distributorId = params.distributorId;
    }

    if (role === 'dealer' && uid) {
      body.dealerId = uid;
    } else if (params?.dealerId) {
      body.dealerId = params.dealerId;
    }

    if (role === 'agent' && uid) {
      body.agentId = uid;
    } else if (params?.agentId) {
      body.agentId = params.agentId;
    }

    return ApiCall("partner/new-debit-transactions", "POST", JSON.stringify(body), {});
  },

  // Fitment certificate PDF for one vehicle/chassis number.
  // The org id is filled from the logged-in user's own role; admin sends all blank.
  // Returns a Blob on success and throws an Error carrying the server message otherwise.
  async DownloadFitmentPdf(params) {
    let userDetail = {};
    try {
      userDetail = JSON.parse(localStorage.getItem('UserDetail') || localStorage.getItem('user') || '{}');
    } catch (e) { }

    const role = userDetail?.role;
    const uid = userDetail?._id || '';

    const body = {
      vehicle_number: params?.vehicle_number || '',
      chassis_number: params?.chassis_number || '',
      distributor: role === 'distributor' ? uid : '',
      dealer: role === 'dealer' ? uid : '',
      agent: role === 'agent' ? uid : '',
    };

    const response = await fetch(`${BASE_URL}partner/fitment-pdf`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await AccessToken()) && { Authorization: await AccessToken() },
      },
      body: JSON.stringify(body),
    });

    const contentType = response.headers.get('content-type') || '';

    // A failure (or a JSON-wrapped response) comes back as JSON, not a PDF.
    if (!response.ok || !contentType.includes('pdf')) {
      let data = {};
      try {
        data = await response.json();
      } catch (e) { }

      if (response.status === 401 && (
        data?.code === 'SESSION_EXPIRED' ||
        data?.code === 'ACCOUNT_DEACTIVATED' ||
        data?.message === 'Session expired. Please Logout and login again.' ||
        data?.error === 'Session expired. Please login again.'
      )) {
        ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
        setTimeout(() => { handleLogout(); }, 1000);
      }

      throw new Error(
        (typeof data?.message === 'string' && data.message.replace(/<[^>]*>?/gm, '').trim()) ||
        data?.error ||
        `Request failed (${response.status})`
      );
    }

    return await response.blob();
  },

  // Per-tag transactions for an activated FASTag (distributor/dealer/agent, permission-gated).
  async GetFastagTransactions(params) {
    return ApiCall("inventory/fastag-transactions", "POST", JSON.stringify({
      pageNumber: params?.pageNumber || 1,
      pageSize: params?.pageSize || 10,
      fastagId: params?.fastagId || "",
    }), {});
  },

  // Distributor Master FASTag — scoped to the logged-in distributor's tree.
  async GetDistributorMasterFastag(params) {
    let url = `inventory/distributor-master?page=${params?.page || 1}&limit=${params?.limit || 10}`;
    if (params?.status && params?.status !== '') {
      url += `&status=${params.status}`;
    }
    url += `&search=${params?.search || ''}`;
    url += `&startDate=${params?.startDate || ''}`;
    url += `&endDate=${params?.endDate || ''}`;
    url += `&dealer=${params?.dealer || ''}`;
    url += `&agent=${params?.agent || ''}`;
    url += `&VC=${params?.VC || ''}`;
    url += `&isActivated=${params?.isActivated || ''}`;
    // When showing only activated tags, sort by activation date (direction toggleable).
    if (params?.isActivated === 'true') {
      url += `&activationSort=true`;
      if (params?.activationSortOrder) {
        url += `&activationSortOrder=${params.activationSortOrder}`;
      }
    }
    return ApiCall(url, "GET", null, {});
  },

  async ExportDistributorMasterInventory(params) {
    let url = `inventory/distributor-master?export=true`;
    if (params?.status && params?.status !== '') {
      url += `&status=${params.status}`;
    }
    url += `&search=${params?.search || ''}`;
    url += `&startDate=${params?.startDate || ''}`;
    url += `&endDate=${params?.endDate || ''}`;
    url += `&dealer=${params?.dealer || ''}`;
    url += `&agent=${params?.agent || ''}`;
    url += `&VC=${params?.VC || ''}`;
    if (params?.isActivated && params?.isActivated !== '') {
      url += `&isActivated=${params.isActivated}`;
    }

    const response = await fetch(`${BASE_URL}${url}`, {
      method: "GET",
      headers: {
        ...(await AccessToken()) && { Authorization: await AccessToken() },
      },
    });

    if (!response.ok) {
      throw new Error("Failed to export distributor master inventory");
    }
    return await response.blob();
  },
  async CreateDistributorBulk(params) {
    return ApiCall("users/distributor/bulckcreate", "POST", params, {});
  },

  async CreateAgentBulk(params) {
    return ApiCall("users/agent/bulkcreate", "POST", params, {});
  },

  async GetDistributorSummary() {
    return ApiCall("inventory/distributorSummary", "GET", null, {});
  },
  async GetDistributorAgentSummary(params) {
    let url = "inventory/distributoragentSummary?";

    if (params?.thisYear) {
      url += `thisYear=${params.thisYear}`;
    } else if (params?.thisMonth) {
      url += `thisMonth=${params.thisMonth}`;
    } else if (params?.LastMonth) {
      url += `LastMonth=${params.LastMonth}`;
    } else {
      url += "thisYear=true";
    }

    return ApiCall(url, "GET", null, {});
  },
  async GetDistributorDealerSummary(params) {
    let url = "inventory/distributordealerSummary?";

    if (params?.thisYear) {
      url += `thisYear=${params.thisYear}`;
    } else if (params?.thisMonth) {
      url += `thisMonth=${params.thisMonth}`;
    } else if (params?.LastMonth) {
      url += `LastMonth=${params.LastMonth}`;
    } else {
      url += "thisYear=true";
    }

    return ApiCall(url, "GET", null, {});
  },
  async GetAgentSummary() {
    return ApiCall("inventory/agentSummary", "GET", null, {});
  },

  async PurchageFastagAgentRequest(params) {
    // Handle both FormData (with file) and JSON (without file)
    if (params instanceof FormData) {
      const response = await fetch(`${BASE_URL}agent/purchageFastagAgentRequest`, {
        method: "POST",
        headers: {
          ...(await AccessToken()) && { Authorization: await AccessToken() },
          // Don't set Content-Type for FormData - browser will set it with boundary
        },
        body: params,
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        data = {};
      }

      if (response.status === 401) {
        if (
          data?.code === 'SESSION_EXPIRED' ||
          data?.code === 'ACCOUNT_DEACTIVATED' ||
          data?.message === 'Session expired. Please Logout and login again.' ||
          data?.error === 'Session expired. Please login again.'
        ) {
          ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
          setTimeout(() => {
            handleLogout();
          }, 1000);
        }
      }

      return {
        ...data,
        status: response.status
      };
    } else {
      // For JSON data
      return ApiCall("agent/purchageFastagAgentRequest", "POST", params, {});
    }
  },
  async GetDistributeExcelInventory(params) {
    const response = await fetch(`${BASE_URL}inventory/getDistributeExcelInventory`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await AccessToken()) && { Authorization: await AccessToken() },
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      if (response.status === 401) {
        try {
          const data = await response.json();
          if (
            data?.code === 'SESSION_EXPIRED' ||
            data?.code === 'ACCOUNT_DEACTIVATED' ||
            data?.message === 'Session expired. Please Logout and login again.' ||
            data?.error === 'Session expired. Please login again.'
          ) {
            ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
            setTimeout(() => {
              handleLogout();
            }, 1000);
          }
        } catch (e) { }
      }
      throw new Error("Sample file download failed");
    }

    return await response.blob(); // return blob for file download
  },

  async AssignAgentExcel(formData) {
    const response = await fetch(`${BASE_URL}inventory/assignAgentExcelInventory`, {
      method: "POST",
      headers: {
        ...(await AccessToken()) && { Authorization: await AccessToken() },
        // Don't set Content-Type for FormData - browser will set it with boundary
      },
      body: formData,
    });

    let data;
    try {
      data = await response.json();
    } catch (e) {
      data = {};
    }

    if (response.status === 401) {
      if (
        data?.code === 'SESSION_EXPIRED' ||
        data?.code === 'ACCOUNT_DEACTIVATED' ||
        data?.message === 'Session expired. Please Logout and login again.' ||
        data?.error === 'Session expired. Please login again.'
      ) {
        ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
        setTimeout(() => {
          handleLogout();
        }, 1000);
      }
    }

    return {
      ...data,
      status: response.status
    };
  },

  // Distributor -> Dealer bulk assignment via Excel (re-homes stock to the dealer).
  async AssignDealerExcel(formData) {
    const response = await fetch(`${BASE_URL}inventory/assignDealerExcelInventory`, {
      method: "POST",
      headers: {
        ...(await AccessToken()) && { Authorization: await AccessToken() },
        // Don't set Content-Type for FormData - browser will set it with boundary
      },
      body: formData,
    });

    let data;
    try {
      data = await response.json();
    } catch (e) {
      data = {};
    }

    if (response.status === 401) {
      if (
        data?.code === 'SESSION_EXPIRED' ||
        data?.code === 'ACCOUNT_DEACTIVATED' ||
        data?.message === 'Session expired. Please Logout and login again.' ||
        data?.error === 'Session expired. Please login again.'
      ) {
        ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
        setTimeout(() => {
          handleLogout();
        }, 1000);
      }
    }

    return {
      ...data,
      status: response.status
    };
  },

  async GetAllFastagAgentRequest(params) {
    return ApiCall(`agent/getAllfastagAgentRequest?page=${params?.page || 1}&limit=${params?.limit || 10}&userRole=${params?.userRole || ''}&scope=${params?.scope || ''}`, "GET", null, {});
  },

  async GetAgentFastag(params) {
    return ApiCall(`agent/getagentFastag?page=${params?.page || 1}&limit=${params?.limit || 10}`, "GET", null, {});
  },

  async UpdateAgentRequestStatus(params) {
    return ApiCall("agent/updateRequestStatus", "POST", params, {});
  },

  async DeniedRequest(params) {
    return ApiCall("purchageFastag/DeniedRequest", "POST", params, {});
  },

  async ExportMasterInventory(params) {
    // masterExcel (not undoExcel): the undo flag returns the Inventory Movement
    // template — a different sheet, with manual-fill columns and no Dealer tier.
    // This one returns the admin table's own columns.
    let url = `inventory?masterExcel=true`;
    if (params?.status && params?.status !== '') {
      url += `&status=${params.status}`;
    }
    url += `&search=${params?.search || ''}`;
    url += `&startDate=${params?.startDate || ''}`;
    url += `&endDate=${params?.endDate || ''}`;
    url += `&distributor=${params?.distributor || ''}`;
    url += `&agent=${params?.agent || ''}`;
    url += `&dealer=${params?.dealer || ''}`;
    url += `&VC=${params?.VC || ''}`;
    // Mirrors GetMasterFastag so the exported file matches the filtered list,
    // in the same order the table is showing.
    url += `&isActivated=${params?.isActivated || ''}`;
    if (params?.isActivated === 'true') {
      url += `&activationSort=true`;
      if (params?.activationSortOrder) {
        url += `&activationSortOrder=${params.activationSortOrder}`;
      }
    }

    // Exporting every matching row is a long request; without a ceiling a stalled
    // backend leaves the button spinning until the browser gives up.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 120000);

    let response;
    try {
      response = await fetch(`${BASE_URL}${url}`, {
        method: "GET",
        headers: {
          ...(await AccessToken()) && { Authorization: await AccessToken() },
        },
        signal: controller.signal,
      });
    } catch (e) {
      throw new Error(
        e?.name === 'AbortError'
          ? 'The export timed out. Narrow the filters and try again.'
          : 'Could not reach the server. Check your connection and try again.'
      );
    } finally {
      clearTimeout(timeout);
    }

    if (!response.ok) {
      if (response.status === 401) {
        try {
          const data = await response.json();
          if (
            data?.code === 'SESSION_EXPIRED' ||
            data?.code === 'ACCOUNT_DEACTIVATED' ||
            data?.message === 'Session expired. Please Logout and login again.' ||
            data?.error === 'Session expired. Please login again.'
          ) {
            ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
            setTimeout(() => {
              handleLogout();
            }, 1000);
          }
        } catch (e) { }
      }
      // Surface what the server actually said ("No inventory records match the
      // current filters.") instead of a blanket failure.
      let serverMessage = '';
      try {
        const data = await response.json();
        serverMessage = data?.message || data?.error || '';
      } catch (e) { }
      throw new Error(serverMessage || `Export failed (${response.status})`);
    }

    // A backend without the masterExcel branch ignores the flag and answers 200
    // with the normal paginated JSON. Saving that as .xlsx produces a file that
    // only fails when opened, so reject anything that isn't a spreadsheet.
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('spreadsheetml') && !contentType.includes('octet-stream')) {
      throw new Error('The server did not return a spreadsheet. Please contact support.');
    }

    return await response.blob();
  },

  async ExportAdminAssignInventory(params) {
    let url = `inventory?adminInventoryExcel=true`;
    if (params?.status && params?.status !== '') {
      url += `&status=${params.status}`;
    }
    url += `&search=${params?.search || ''}`;
    url += `&startDate=${params?.startDate || ''}`;
    url += `&endDate=${params?.endDate || ''}`;
    url += `&distributorId=${params?.distributorId || ''}`;
    url += `&agent=${params?.agent || ''}`;
    url += `&VC=${params?.VC || ''}`;

    const response = await fetch(`${BASE_URL}${url}`, {
      method: "GET",
      headers: {
        ...(await AccessToken()) && { Authorization: await AccessToken() },
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        try {
          const data = await response.json();
          if (
            data?.code === 'SESSION_EXPIRED' ||
            data?.code === 'ACCOUNT_DEACTIVATED' ||
            data?.message === 'Session expired. Please Logout and login again.' ||
            data?.error === 'Session expired. Please login again.'
          ) {
            ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
            setTimeout(() => {
              handleLogout();
            }, 1000);
          }
        } catch (e) { }
      } else if (response.status === 404) {
        throw new Error("No inventory records found");
      }
      throw new Error("Export failed");
    }

    return await response.blob();
  },

  // Admin-only multi-tier file reverse (Distributor->Admin, Dealer->Distributor,
  // Agent->Distributor, Agent->Dealer). Separate endpoint from UndoInventoryExcel,
  // which keeps its original single-tier behaviour.
  async AdminUndoInventoryExcel(formData) {
    const response = await fetch(`${BASE_URL}inventory/adminUndoExcelInventory`, {
      method: 'POST',
      headers: {
        ...(await AccessToken()) && { Authorization: await AccessToken() },
      },
      body: formData,
    });
    let data;
    try { data = await response.json(); } catch (e) { data = {}; }
    return { ...data, status: response.status };
  },

  async UndoInventoryExcel(formData) {
    const response = await fetch(`${BASE_URL}inventory/undoExcelInventory`, {
      method: "POST",
      headers: {
        ...(await AccessToken()) && { Authorization: await AccessToken() },
      },
      body: formData,
    });

    let data;
    try {
      data = await response.json();
    } catch (e) {
      data = {};
    }

    if (response.status === 401) {
      if (
        data?.code === 'SESSION_EXPIRED' ||
        data?.code === 'ACCOUNT_DEACTIVATED' ||
        data?.message === 'Session expired. Please Logout and login again.' ||
        data?.error === 'Session expired. Please login again.'
      ) {
        ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
        setTimeout(() => {
          handleLogout();
        }, 1000);
      }
    }

    return {
      ...data,
      status: response.status
    };
  },

  // Manual reverse (undo): list the requester's reversible FASTags.
  async GetReversibleInventory(params) {
    const query = new URLSearchParams();
    query.append("page", params?.page || 1);
    query.append("limit", params?.limit || 10);
    if (params?.search) query.append("search", params.search);
    // Name search over the tier below the caller (distributor / dealer / agent,
    // depending on the caller's own role — the backend decides which).
    if (params?.childName) query.append("childName", params.childName);
    // Admin can walk down the tree: which tier's holdings to list, and which
    // distributor / dealer / agent to scope them to.
    if (params?.tier) query.append("tier", params.tier);
    if (params?.distributorId) query.append("distributorId", params.distributorId);
    if (params?.dealerId) query.append("dealerId", params.dealerId);
    if (params?.agentId) query.append("agentId", params.agentId);
    return ApiCall(`inventory/reversible?${query.toString()}`, "GET", null, {});
  },

  // Manual reverse (undo): reverse a selected set of inventory ids.
  async UndoManualInventory(params) {
    return ApiCall("inventory/undoManualInventory", "POST", JSON.stringify({ inventoryIds: params?.inventoryIds || [] }), {});
  },

  async GetAdminExcelInventory(params) {
    const response = await fetch(`${BASE_URL}inventory/getAdminExcelInventory`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await AccessToken()) && { Authorization: await AccessToken() },
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      if (response.status === 401) {
        try {
          const data = await response.json();
          if (
            data?.code === 'SESSION_EXPIRED' ||
            data?.code === 'ACCOUNT_DEACTIVATED' ||
            data?.message === 'Session expired. Please Logout and login again.' ||
            data?.error === 'Session expired. Please login again.'
          ) {
            ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
            setTimeout(() => {
              handleLogout();
            }, 1000);
          }
        } catch (e) { }
      }
      throw new Error("Sample file download failed");
    }

    return await response.blob();
  },

  async AssignDistributorExcel(formData) {
    const response = await fetch(`${BASE_URL}inventory/assignDistributorExcelInventory`, {
      method: "POST",
      headers: {
        ...(await AccessToken()) && { Authorization: await AccessToken() },
      },
      body: formData,
    });

    let data;
    try {
      data = await response.json();
    } catch (e) {
      data = {};
    }

    if (response.status === 401) {
      if (
        data?.code === 'SESSION_EXPIRED' ||
        data?.code === 'ACCOUNT_DEACTIVATED' ||
        data?.message === 'Session expired. Please Logout and login again.' ||
        data?.error === 'Session expired. Please login again.'
      ) {
        ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
        setTimeout(() => {
          handleLogout();
        }, 1000);
      }
    }

    return {
      ...data,
      status: response.status
    };
  },

  async GetUndoSampleExcel() {
    const response = await fetch(`${BASE_URL}inventory/getUndoSampleExcel`, {
      method: "GET",
      headers: {
        ...(await AccessToken()) && { Authorization: await AccessToken() },
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        try {
          const data = await response.json();
          if (
            data?.code === 'SESSION_EXPIRED' ||
            data?.code === 'ACCOUNT_DEACTIVATED' ||
            data?.message === 'Session expired. Please Logout and login again.' ||
            data?.error === 'Session expired. Please login again.'
          ) {
            ErrorToast(data?.message || 'Session expired. Please login again.');
            setTimeout(() => {
              handleLogout();
            }, 1000);
          }
        } catch (e) { }
      }
      throw new Error("Sample file download failed");
    }

    return await response.blob();
  },
  async GetAgentsDistributorWise(params) {
    return ApiCall(`users/get-agents-distributor-wise?page=${params?.page || 1}&limit=${params?.limit || 10}&search=${params?.search || ''}&distributorId=${params?.distributorId || ''}`, "GET", null, {});
  },
  async GetDealersDistributorWise(params) {
    return ApiCall(`users/get-dealers-distributor-wise?page=${params?.page || 1}&limit=${params?.limit || 10}&search=${params?.search || ''}&distributorId=${params?.distributorId || ''}`, "GET", null, {});
  },
  // Admin: delete a dealer/agent (backend refuses while they still hold inventory).
  async DeleteDealerOrAgent(id) {
    return ApiCall(`users/delete-dealer-agent/${id}`, "POST", null, {});
  },
  async GetDealerAgents(params) {
    return ApiCall(`users/dealer-agents?dealerId=${params?.dealerId || ''}`, "GET", null, {});
  },
  async SetActivationPermission(params) {
    return ApiCall("users/set-activation-permission", "POST", JSON.stringify({
      userId: params?.userId,
      canActivateFastag: params?.canActivateFastag,
    }), {});
  },

  // Support tickets
  async CreateSupportTicket(params) {
    return ApiCall("support", "POST", JSON.stringify({
      subject: params?.subject,
      description: params?.description,
      vehicleNumber: params?.vehicleNumber,
    }), {});
  },
  async GetSupportTickets(params) {
    return ApiCall(`support?page=${params?.page || 1}&limit=${params?.limit || 10}&status=${params?.status || ''}&search=${params?.search || ''}`, "GET", null, {});
  },
  async CloseSupportTicket(id, params) {
    return ApiCall(`support/${id}/close`, "POST", JSON.stringify({ remark: params?.remark || '' }), {});
  },
  async DownloadTabWiseExcel(params) {
    let url = `inventory/downloadTabWiseExcel?tab=${params?.tab || 'available'}`;
    if (params?.status && params?.status !== '') {
      url += `&status=${params.status}`;
    }
    url += `&search=${params?.search || ''}`;
    url += `&startDate=${params?.startDate || ''}`;
    url += `&endDate=${params?.endDate || ''}`;
    url += `&distributor=${params?.distributor || ''}`;
    url += `&agent=${params?.agent || ''}`;
    url += `&dealer=${params?.dealer || ''}`;
    url += `&VC=${params?.VC || ''}`;
    url += `&userActivationStatus=${params?.userActivationStatus || ''}`;
    url += `&isActivated=${params?.isActivated || ''}`;

    const response = await fetch(`${BASE_URL}${url}`, {
      method: "GET",
      headers: {
        ...(await AccessToken()) && { Authorization: await AccessToken() },
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        try {
          const data = await response.json();
          if (
            data?.code === 'SESSION_EXPIRED' ||
            data?.code === 'ACCOUNT_DEACTIVATED' ||
            data?.message === 'Session expired. Please Logout and login again.' ||
            data?.error === 'Session expired. Please login again.'
          ) {
            ErrorToast(data?.message || data?.error || 'Session expired. Please login again.');
            setTimeout(() => {
              handleLogout();
            }, 1000);
          }
        } catch (e) { }
      }
      throw new Error("File download failed");
    }

    return await response.blob();
  },
  async FetchFastagCost(custRefId) {
    return ApiCall("inventory/fetchFastagCost", "POST", JSON.stringify({ custRefId }), {});
  },
};


